import {
  CreateExtractionUploadRequest,
  CreateJobRequest,
  EXTRACTIONS_PATH,
  extractionPagePath,
  extractionPath,
  ExtractionSummary,
  extractionSummarySchema,
  ExtractionUploadResponse,
  extractionUploadResponseSchema,
  Job,
  jobCancelPath,
  jobMaterialPagePath,
  jobPath,
  JOBS_PATH,
  jobSchema,
  ListJobsResponse,
  listJobsResponseSchema,
  MaterialPageResponse,
  materialPageResponseSchema,
  SPEND_PATH,
  SpendResponse,
  spendResponseSchema,
} from '@quizdb/common';
import { quizdbApi } from '../api/quizdbApi';

/** The generation jobs' endpoints (QZ-D48, QZ-D55, QZ-D57). */
export const jobApi = quizdbApi.injectEndpoints({
  endpoints: (builder) => ({
    listJobs: builder.infiniteQuery<ListJobsResponse, void, string | undefined>({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (last) => last.nextCursor,
      },
      query: ({ pageParam }) => ({
        url: JOBS_PATH,
        params: pageParam === undefined ? {} : { cursor: pageParam },
      }),
      transformResponse: (response: unknown) => listJobsResponseSchema.parse(response),
      providesTags: ['JobList'],
    }),
    getJob: builder.query<Job, string>({
      query: (jobId) => jobPath(jobId),
      transformResponse: (response: unknown) => jobSchema.parse(response),
      providesTags: (_result, _error, jobId) => [{ type: 'Job', id: jobId }],
    }),
    createJob: builder.mutation<Job, CreateJobRequest>({
      query: (request) => ({ url: JOBS_PATH, method: 'POST', body: request }),
      transformResponse: (response: unknown) => jobSchema.parse(response),
      invalidatesTags: ['JobList', 'Spend'],
    }),
    cancelJob: builder.mutation<Job, string>({
      query: (jobId) => ({ url: jobCancelPath(jobId), method: 'POST' }),
      transformResponse: (response: unknown) => jobSchema.parse(response),
      invalidatesTags: (_result, _error, jobId) => [{ type: 'Job', id: jobId }, 'JobList'],
    }),
    getSpend: builder.query<SpendResponse, void>({
      query: () => SPEND_PATH,
      transformResponse: (response: unknown) => spendResponseSchema.parse(response),
      providesTags: ['Spend'],
    }),
    // A fresh browser hash may already have an extraction on the API; nothing is cached (PTX-D4,
    // mirroring PDG-D11).
    getExtraction: builder.query<ExtractionSummary, string>({
      query: (id) => extractionPath(id),
      transformResponse: (response: unknown) => extractionSummarySchema.parse(response),
      keepUnusedDataFor: 0,
    }),
    createExtractionUpload: builder.mutation<
      ExtractionUploadResponse,
      CreateExtractionUploadRequest
    >({
      query: (body) => ({ url: EXTRACTIONS_PATH, method: 'POST', body }),
      transformResponse: (response: unknown) => extractionUploadResponseSchema.parse(response),
    }),
    // Page text never changes once extracted, so this keeps RTK Query's default cache lifetime.
    getExtractionPage: builder.query<MaterialPageResponse, { id: string; page: number }>({
      query: ({ id, page }) => extractionPagePath(id, page),
      transformResponse: (response: unknown) => materialPageResponseSchema.parse(response),
    }),
    getJobMaterialPage: builder.query<MaterialPageResponse, { jobId: string; page: number }>({
      query: ({ jobId, page }) => jobMaterialPagePath(jobId, page),
      transformResponse: (response: unknown) => materialPageResponseSchema.parse(response),
    }),
  }),
});

export const {
  useListJobsInfiniteQuery,
  useGetJobQuery,
  useCreateJobMutation,
  useCancelJobMutation,
  useGetSpendQuery,
  useLazyGetExtractionQuery,
  useCreateExtractionUploadMutation,
  useLazyGetExtractionPageQuery,
  useLazyGetJobMaterialPageQuery,
} = jobApi;
